import { useState, useEffect, ReactElement, ReactNode, useMemo, useCallback } from 'react';
import { Tab, TabList, SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import { ControlledTooltip } from './ControlledTooltip';

export type TTabConfig<T extends string = string> = {
  id: T;
  label: string;
  content: ReactNode;
  icon?: ReactElement;
  count?: number;
  showCount?: boolean;
  /** Whether this tab can be selected automatically. Defaults to true. */
  autoSelect?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
};

type TProps<T extends string = string> = {
  tabs: TTabConfig<T>[];
  defaultTab?: T;
  autoSelectFirstNonEmpty?: boolean;
  action?: ReactNode;
};

/**
 * A reusable tabbed container for displaying different content views.
 * Supports auto-selection of first non-empty tab and disabled tabs with tooltips.
 */
export const TabbedContent = <T extends string = string>({
  tabs,
  defaultTab,
  autoSelectFirstNonEmpty = false,
  action,
}: TProps<T>) => {
  const initialTab = useMemo((): T => {
    if (autoSelectFirstNonEmpty) {
      const firstNonEmpty = tabs.find((tab) => tab.autoSelect !== false && !tab.disabled && (tab.count ?? 1) > 0);
      if (firstNonEmpty) return firstNonEmpty.id;
    }
    return defaultTab ?? tabs[0]?.id;
  }, [tabs, defaultTab, autoSelectFirstNonEmpty]);

  const [selectedTab, setSelectedTab] = useState<T>(initialTab);

  // Update selected tab if current selection becomes empty/disabled and autoSelect is enabled
  useEffect(() => {
    if (!autoSelectFirstNonEmpty) return;

    const currentTab = tabs.find((tab) => tab.id === selectedTab);
    const isCurrentEmpty = currentTab?.disabled || (currentTab?.count ?? 1) === 0;

    if (isCurrentEmpty) {
      const firstNonEmpty = tabs.find((tab) => tab.autoSelect !== false && !tab.disabled && (tab.count ?? 1) > 0);
      if (firstNonEmpty) {
        setSelectedTab(firstNonEmpty.id);
      }
    }
  }, [tabs, autoSelectFirstNonEmpty, selectedTab]);

  const handleTabSelect = useCallback((_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as T);
  }, []);

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 overflow-x-auto">
          <TabList size="medium" selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            {tabs.map((tab) => {
              const isDisabled = tab.disabled ?? ((tab.count ?? 1) === 0 && !!tab.disabledTooltip);
              const label = tab.showCount && tab.count !== undefined ? `${tab.label} (${tab.count})` : tab.label;

              return (
                <ControlledTooltip
                  key={tab.id}
                  content={tab.disabledTooltip || ''}
                  enable={isDisabled && !!tab.disabledTooltip}
                  relationship="description"
                  withArrow
                  positioning="above"
                >
                  <Tab value={tab.id} disabled={isDisabled} icon={tab.icon}>
                    {label}
                  </Tab>
                </ControlledTooltip>
              );
            })}
          </TabList>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>

      {tabs.find((tab) => tab.id === selectedTab)?.content}
    </div>
  );
};
